import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Typography, TextField, InputAdornment, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";

import {
  HEALTH_STATUS_COLORS,
  VISIT_COLORS,
  SP_VISIT_COLORS,
  getSpVisitAge,
} from "../../constants/clinicalDashboard";
import ContactDialog from "./ContactDialog";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

const CONTACT_STATUS_COLORS = {
  "Not Contacted": { bg: "#FEE2E2", text: "#991B1B" },
  "Contacted":     { bg: "#DBEAFE", text: "#1D4ED8" },
  "Scheduled":     { bg: "#FEF3C7", text: "#92400E" },
  "No Answer":     { bg: "#F1F5F9", text: "#64748B" },
  "Visited":       { bg: "#DCFCE7", text: "#166534" },
};

/**
 * Tabla de pacientes para outreach con filtros por contacto, clinic visit status.
 *
 * @param {{ patients: Array, filter: "all"|"phone"|"email", onCreateTask: Function }} props
 */
export default function OutreachTable({ patients = [], filter, onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [visitFilter, setVisitFilter] = useState("all");
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactPatient, setContactPatient] = useState(null);

  const handleOpenContact = (row) => {
    setContactPatient(row);
    setContactDialogOpen(true);
  };

  // Debounce search 300ms
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Filtrar según canal de contacto + búsqueda + clinic visit ──
  const filteredRows = useMemo(() => {
    let rows = patients;
    if (filter === "phone") rows = rows.filter((p) => p.hasPhone);
    if (filter === "email") rows = rows.filter((p) => p.hasEmail);
    if (visitFilter === "visited") rows = rows.filter((p) => p.visitedClinic);
    if (visitFilter === "not_visited") rows = rows.filter((p) => !p.visitedClinic);
    if (visitFilter === "contacted") rows = rows.filter((p) => p.timesContacted > 0);
    if (search) {
      const lowerSearch = search.toLowerCase();
      rows = rows.filter((p) => p.name?.toLowerCase().includes(lowerSearch));
    }
    return rows;
  }, [patients, filter, search, visitFilter]);

  // ─── Columnas ──────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Patient",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => (
          <Typography variant="body2" fontWeight={600} noWrap>
            {value}
          </Typography>
        ),
      },
      {
        field: "timeSinceVisit",
        headerName: "Time Since Visit",
        width: 125,
        renderCell: ({ value }) => {
          const palette = VISIT_COLORS[value] || {
            bg: "#F1F5F9",
            text: "#475569",
          };
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                bgcolor: palette.bg,
                color: palette.text,
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          );
        },
      },
      {
        field: "visitedClinic",
        headerName: "Visited Clinic",
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) =>
          value ? (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label="Yes"
              size="small"
              sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#DCFCE7", color: "#166534" }}
            />
          ) : (
            <Chip
              icon={<CancelIcon sx={{ fontSize: 14 }} />}
              label="No"
              size="small"
              sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: "#FEE2E2", color: "#991B1B" }}
            />
          ),
      },
      {
        field: "contactStatus",
        headerName: "Contact Status",
        width: 115,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => {
          const colors = CONTACT_STATUS_COLORS[value] || { bg: "#F1F5F9", text: "#64748B" };
          return (
            <Chip
              label={value || "—"}
              size="small"
              sx={{ fontWeight: 600, fontSize: "0.68rem", bgcolor: colors.bg, color: colors.text, height: 22 }}
            />
          );
        },
      },
      {
        field: "timesContacted",
        headerName: "# Contacts",
        width: 85,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => {
          const val = value || 0;
          if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
          return (
            <Chip
              label={val}
              size="small"
              sx={{
                height: 22,
                minWidth: 28,
                fontSize: "0.72rem",
                fontWeight: 700,
                bgcolor: val >= 3 ? "#DBEAFE" : "#F1F5F9",
                color: val >= 3 ? "#1D4ED8" : "#475569",
              }}
            />
          );
        },
      },
      getObservationsColumn(showObservationsModal, 3),
      {
        field: "viewPatient",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Tooltip title="View Patient">
            <IconButton size="small" onClick={() => navigate(`/patient-info/${row.cipId}`)} sx={{ color: "#6366F1" }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: "contactInfo",
        headerName: "Contact",
        width: 95,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContactPhoneIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleOpenContact(row)}
            sx={{ textTransform: "none", fontSize: "0.72rem" }}
          >
            Contact
          </Button>
        ),
      },
      {
        field: "healthStatus",
        headerName: "Status",
        width: 110,
        renderCell: ({ value }) => {
          const palette = HEALTH_STATUS_COLORS[value] || HEALTH_STATUS_COLORS["No Status"];
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                bgcolor: palette.bg,
                color: palette.text,
                border: `1px solid ${palette.border}`,
                fontWeight: 600,
                fontSize: "0.68rem",
              }}
            />
          );
        },
      },
      {
        field: "lastSantePlusVisit",
        headerName: "SP Visit",
        width: 90,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => {
          const age = getSpVisitAge(value);
          const colors = SP_VISIT_COLORS[age];
          if (!value) {
            return <Chip label="—" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
          }
          const d = new Date(value);
          const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        },
      },
      {
        field: "observationsCount",
        headerName: "Obs",
        width: 55,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => {
          const val = value || 0;
          if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
          return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
        },
      },
      {
        field: "activeTasks",
        headerName: "Tasks",
        width: 55,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => {
          const val = value || 0;
          if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
          return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
        },
      },
      {
        field: "action",
        headerName: "Action",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onCreateTask(row)}
            sx={{ textTransform: "none", fontSize: "0.72rem" }}
          >
            Log Call
          </Button>
        ),
      },
    ],
    [onCreateTask, handleOpenContact, navigate, showObservationsModal],
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Search + Visit filter */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search patient..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            minWidth: 200,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />
        {["all", "visited", "not_visited", "contacted"].map((f) => (
          <Chip
            key={f}
            label={
              f === "all" ? "All" :
              f === "visited" ? "Visited Clinic" :
              f === "not_visited" ? "Not Visited" :
              "Contacted"
            }
            size="small"
            onClick={() => setVisitFilter(f)}
            variant={visitFilter === f ? "filled" : "outlined"}
            color={visitFilter === f ? "primary" : "default"}
            sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.72rem" }}
          />
        ))}
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": { fontSize: "0.78rem" },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.78rem",
          },
        }}
      />

      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        patient={contactPatient}
      />
    </Box>
  );
}
